import { autoinject, computedFrom } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { LookupValueRequest, LookupValue } from "../../../../../../models/lookups/lookup.models";
import { GhgService } from "../../../../../../services/ghg/ghg.service";
import {
    GhgVerificationRatingFactorClientModel, GhgVerificationEnergySavingEquipmentModel,
    GhgVerificationSisteVesselModel, GhgVerificationStatusEnum, GhgVerificationClientModel, GhgVerificationEexiFactorClientModel, GhgVerificationCiiFactorClientModel
} from "../../../../../../models/ghgs/ghg.models";
import { LookupValueQuery } from "../../../../../../constants/modules/lookup.constants";
import { GhgFactorsRenderer } from "../../helpers/ghg-factors-renderer";
import { GhgEexiFactorsRenderer } from "../../helpers/ghg-eexi-factors-renderer";
import { GhgCiiFactorsRenderer } from "../../helpers/ghg-cii-factors-renderer";
import { LookupControl } from "../../../../../components/lookups/lookup-control/lookup-control";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";

@autoinject
export class GhgVerificationStatusDialog {

    public sendEmails: boolean = true;
    public isDropdownOpen: boolean;
    public ghgVerificationStatuses: LookupValue[] = [];
    public currentGhgVerificationStatus: LookupValue;
    public meta: any = {};
    public ghgVerification: GhgVerificationClientModel;
    public ghgVerificationStatusId: number;
    public setGhgVerificationStatus: any;
    public vesselName: string;
    public europeanNumberOfIdentification: string;
    public vehicleIdentificationNumber: string;
    public ghgVerificationRatingFactors: GhgVerificationRatingFactorClientModel[] = [];
    public ghgVerificationEexiFactors: GhgVerificationEexiFactorClientModel[] = [];
    public ghgVerificationCiiFactors: GhgVerificationCiiFactorClientModel[] = [];
    public ghgVerificationEnergySavingEquipments: GhgVerificationEnergySavingEquipmentModel[] = [];
    public sisterVessels: GhgVerificationSisteVesselModel[];
    public currentRatingsSummary: any;
    public revisedRatingsSummary: any;
    public awaitingResponse: number = GhgVerificationStatusEnum.AwaitingResponse;
    public accepted: number = GhgVerificationStatusEnum.Accepted;
    public rejected: number = GhgVerificationStatusEnum.Rejected;
    public inProgress: number = GhgVerificationStatusEnum.InProgress;
    public comments: string;
    public statusSnippet: string;
    public onSubmit: () => void;
    public subtaskTypes: number[] = [];
    public ghgFactorsRenderer: GhgFactorsRenderer;
    public ghgEexiFactorsRenderer: GhgEexiFactorsRenderer;
    public ghgCiiFactorsRenderer: GhgCiiFactorsRenderer;
    public vesselId: number;
    public verifyEnergySavingEquipment: boolean;
    public ghgVerificationEvdiStatusId: number = null;
    public previousGhgVerificationStatusId: number;
    public loading: boolean;
    public yardNumber: string;
    public mainEngineModel: string;
    public outcomes: { name: string, value: boolean }[] = [];
    public ghgVerificationStatusLookup: LookupControl;
    public showGHG: boolean;

    @computedFrom('ghgVerificationEnergySavingEquipments')
    public get passedGhgVerificationEnergySavingEquipments(): GhgVerificationEnergySavingEquipmentModel[] {
        return this.ghgVerificationEnergySavingEquipments.filter(x => x.passed === true);
    }

    @computedFrom('currentGhgVerificationStatus', 'ghgVerification.revisedGhgRatingInProgress')
    get submitEnabled(): boolean {
        return this.currentGhgVerificationStatus &&
            this.ghgVerificationStatusId != this.currentGhgVerificationStatus.id &&
            (!this.ghgVerification.revisedGhgRatingInProgress || [GhgVerificationStatusEnum.Accepted, GhgVerificationStatusEnum.Rejected].indexOf(this.currentGhgVerificationStatus.id) == -1);
    }

    @computedFrom('currentGhgVerificationStatus')
    get showGhgData() {
        if (this.currentGhgVerificationStatus) {
            switch (this.currentGhgVerificationStatus.id) {
                case GhgVerificationStatusEnum.Accepted:
                case GhgVerificationStatusEnum.Rejected:
                case GhgVerificationStatusEnum.Cancelled:
                    return true;
            }
        }

        return false;
    }

    @computedFrom('currentGhgVerificationStatus')
    get showRevisedGhgData() {
        if (this.currentGhgVerificationStatus) {
            switch (this.currentGhgVerificationStatus.id) {
                case GhgVerificationStatusEnum.Accepted:
                case GhgVerificationStatusEnum.Rejected:
                    return true;
            }
        }

        return false;
    }

    @computedFrom('previousGhgVerificationStatusId')
    get wasClosed() {
        const statuses = [
            GhgVerificationStatusEnum.Accepted,
            GhgVerificationStatusEnum.Rejected,
            GhgVerificationStatusEnum.Cancelled
        ];

        return statuses.indexOf(this.previousGhgVerificationStatusId) != -1;
    }

    @computedFrom('currentGhgVerificationStatus')
    get commentsLabel() {
        if (this.currentGhgVerificationStatus) {
            switch (this.currentGhgVerificationStatus.id) {
                case GhgVerificationStatusEnum.Accepted:
                case GhgVerificationStatusEnum.Rejected:
                case GhgVerificationStatusEnum.Cancelled:
                case GhgVerificationStatusEnum.Expired:
                    return "Final Comments";
            }
        }

        return "Other Comments"
    }

    @computedFrom('ghgVerificationEvdiStatusId', 'ghgVerificationStatusLookup', 'currentRatingsSummary')
    public get isRevisedGhgVerified(): boolean {
        if (!this.ghgVerificationStatusLookup) {
            return false;
        }
        const isStatusVerified = this.ghgVerificationStatusLookup.selectedItem.additionalData.isVerified;
        return isStatusVerified || (isStatusVerified === null && this.currentRatingsSummary && this.currentRatingsSummary.verified);
    }

    constructor(private dialogClientService: DialogClientService,
        private lookupClientService: LookupClientService,
        private ghgService: GhgService,
        userClaimsService: UserClaimsService) {
        this.showGHG = userClaimsService.hasPermission(Claims.ShowGHG);
    }


    async activate(model) {

        this.loading = true;
        this.onSubmit = model.onSubmit;
        this.verifyEnergySavingEquipment = model.verifyEnergySavingEquipment;
        this.ghgVerification = model.ghgVerification;
        this.ghgVerificationStatusId = model.ghgVerificationStatusId;
        this.setGhgVerificationStatus = model.setGhgVerificationStatus;
        this.vesselName = model.vesselName;
        this.vesselId = model.vesselId;
        this.europeanNumberOfIdentification = model.europeanNumberOfIdentification;
        this.vehicleIdentificationNumber = model.vehicleIdentificationNumber;
        this.previousGhgVerificationStatusId = model.previousGhgVerificationStatusId;
        this.subtaskTypes = model.subtaskTypes;

        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.GhgVerificationStatuses;
        const response = await this.lookupClientService.getLookup(request);
        this.ghgVerificationStatuses = response.activeValues;
    }

    public showOutcomeSection(outcome: boolean) {
        return this.ghgVerificationEexiFactors.some(x => x.passed === outcome && x.isVisible) ||
            this.ghgVerificationCiiFactors.some(x => x.passed === outcome && x.isVisible) ||
            this.ghgVerificationRatingFactors.some(x => x.passed === outcome && x.isVisible);
    }

    public getOutcomeTitle(outcome: boolean) {
        const title = outcome === true ? "Passed" : outcome === false ? "Failed" : "N/A";
        const count = (this.ghgVerificationEexiFactors.filter(x => x.passed === outcome && x.isVisible).length +
            this.ghgVerificationCiiFactors.filter(x => x.passed === outcome && x.isVisible).length +
            this.ghgVerificationRatingFactors.filter(x => x.passed === outcome && x.isVisible).length);
        return `${title} (${count})`;
    }

    public getOutcomeClass(outcome: boolean) {
        return outcome === true ? "passed" : outcome === false ? "failed" : "na";
    }

    public getOutcomeIcon(outcome: boolean) {
        return outcome === true ? "tick" : outcome === false ? "cross" : "na";
    }

    public getGhgVerificationRatingFactorsForOutcome(outcome: boolean): GhgVerificationRatingFactorClientModel[] {
        return this.ghgVerificationRatingFactors.filter(x => x.passed === outcome && x.isVisible);
    }

    public getGhgVerificationEexiFactorsForOutcome(outcome: boolean): GhgVerificationEexiFactorClientModel[] {
        return this.ghgVerificationEexiFactors.filter(x => x.passed === outcome && x.isVisible);
    }

    public getGhgVerificationCiiFactorsForOutcome(outcome: boolean): GhgVerificationCiiFactorClientModel[] {
        return this.ghgVerificationCiiFactors.filter(x => x.passed === outcome && x.isVisible);
    }

    async attached() {
        const response = await this.ghgService.getGhgVerificationStatusClientModel(this.ghgVerification.ghgVerificationId);
        this.sisterVessels = response.sisterVessels;

        this.currentRatingsSummary = response.currentRatingsSummary;
        this.revisedRatingsSummary = response.revisedRatingsSummary;
        this.ghgVerificationEvdiStatusId = this.currentRatingsSummary.ghgVerificationEvdiStatusId;

        this.ghgFactorsRenderer = new GhgFactorsRenderer(response.ghgVerificationRatingFactors, this.ghgVerification);
        this.ghgEexiFactorsRenderer = new GhgEexiFactorsRenderer(response.ghgVerificationEexiFactors);
        this.ghgCiiFactorsRenderer = new GhgCiiFactorsRenderer(response.ghgVerificationCiiFactors);

        response.ghgVerificationRatingFactors.forEach(factor => {
            factor.isVisible = this.ghgFactorsRenderer.showFactor(factor) && !this.ghgFactorsRenderer.isFactorReadonly();
        });

        response.ghgVerificationEexiFactors.forEach(factor => {
            factor.isVisible = this.ghgEexiFactorsRenderer.showFactor() && !this.ghgEexiFactorsRenderer.isFactorReadonly(factor);
        });

        response.ghgVerificationCiiFactors.forEach(factor => {
            factor.isVisible = this.ghgCiiFactorsRenderer.showFactor() && !this.ghgCiiFactorsRenderer.isFactorReadonly();
        });

        this.ghgVerificationRatingFactors = response.ghgVerificationRatingFactors;
        this.ghgVerificationEexiFactors = response.ghgVerificationEexiFactors;
        this.ghgVerificationCiiFactors = response.ghgVerificationCiiFactors;
        this.ghgVerificationEnergySavingEquipments = response.ghgVerificationEnergySavingEquipments;

        if (this.wasClosed) {
            this.ghgVerificationStatuses = this.ghgVerificationStatuses.filter(x => x.id == GhgVerificationStatusEnum.InProgress || x.id == this.ghgVerificationStatusId)
        }

        this.currentGhgVerificationStatus = this.ghgVerificationStatuses.filter(x => x.id == (this.wasClosed ? GhgVerificationStatusEnum.InProgress : this.ghgVerificationStatusId))[0];
        this.outcomes = [{
            name: "Passed",
            value: true
        }, {
            name: "Failed",
            value: false
        },
        {
            name: "N/A",
            value: null
        }];
        this.loading = false;
    }

    lastSelectedItemIds: number[] = [];

    selectedItemsChangedForAwaitingResponse($event) {
        const notSelected = $event.detail.filter(x => !x.isSelected);

        if (this.statusSnippet) {
            notSelected.forEach(x => {
                const index = this.lastSelectedItemIds.indexOf(x.id);
                if (index >= 0)
                    this.lastSelectedItemIds.splice(index, 1);
                this.statusSnippet = this.statusSnippet.split(`, ${x.name}`).join("");
                this.statusSnippet = this.statusSnippet.split(`${x.name}, `).join("");
                this.statusSnippet = this.statusSnippet.split(x.name).join("");
            });
        }

        const newItems = $event.detail.filter(x => x.isSelected && this.lastSelectedItemIds.indexOf(x.id) == -1);
        newItems.forEach(x => {
            if (this.statusSnippet && !this.statusSnippet.endsWith(", "))
                this.statusSnippet += ", ";
            else if (!this.statusSnippet) {
                this.statusSnippet = "";
            }

            if (x && x.name)
                this.statusSnippet += x.name;

            this.lastSelectedItemIds.push(x.id);
        });
    }

    closeDialog() {
        this.dialogClientService.hide();
    }

    openDropdown(event) {
        event.stopPropagation();
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    closeDropdown() {
        this.isDropdownOpen = false;
    }

    async reviewStatusChanged($event) {
        if (!$event.detail || ($event.detail.oldValue && $event.detail.value === $event.detail.oldValue) || !$event.detail.oldValue || !$event.detail.value)
            return;

        if (this.currentGhgVerificationStatus && (this.currentGhgVerificationStatus.id == GhgVerificationStatusEnum.Accepted || this.currentGhgVerificationStatus.id == GhgVerificationStatusEnum.Rejected))
            await this.computeRevisedGhg();
    }

    async changeGhgVerificationStatus(ghgVerificationStatus: LookupValue) {
        if (!this.currentGhgVerificationStatus || this.currentGhgVerificationStatus.id != ghgVerificationStatus.id) {
            this.meta = {};
        }
        this.currentGhgVerificationStatus = ghgVerificationStatus;

        if (this.currentGhgVerificationStatus && (this.currentGhgVerificationStatus.id == GhgVerificationStatusEnum.Accepted || this.currentGhgVerificationStatus.id == GhgVerificationStatusEnum.Rejected))
            await this.computeRevisedGhg();
    }

    async computeRevisedGhg() {
        try {
            this.ghgVerification.revisedGhgRatingInProgress = true;
            const response = await this.ghgService.computeRevisedGhg({ ghgVerificationId: this.ghgVerification.ghgVerificationId, ghgVerificationEvdiStatusId: this.ghgVerificationEvdiStatusId });
            if (this.revisedRatingsSummary) {
                this.revisedRatingsSummary.sizeScore = response.revisedSizeScore;
                this.revisedRatingsSummary.rating = response.revisedGhgEmissionRating;
                this.revisedRatingsSummary.score = response.revisedEvdi;
                this.revisedRatingsSummary.verified = response.isRevisedGhgRatingVerified;
                this.revisedRatingsSummary.ratingPlus = response.revisedEmissionRatingPlus;
                this.revisedRatingsSummary.errorMessage = response.revisedGhgRatingError;
                this.revisedRatingsSummary.isEligibleForEVDI = response.revisedIsEligibleForEVDI;
                this.revisedRatingsSummary.hasRating = response.hasRating;
            }
            this.ghgVerification.revisedGhgRatingInProgress = response.revisedGhgRatingInProgress;
        } catch {
            this.revisedRatingsSummary.errorMessage = "Unexpected error, please try to recompute by pressing the button above";
            this.ghgVerification.revisedGhgRatingInProgress = false;
        }
    }

    public filtersHtml(factor: GhgVerificationRatingFactorClientModel, isCurrent: boolean, isEditable: boolean): string {

        const propValue = isCurrent ? "currentValue" : (factor.revisedValue !== null ? "revisedValue" : "submittedValue");

        return this.ghgFactorsRenderer.filtersHtml(factor, propValue, isEditable);
    }

    public filtersEexiHtml(factor: GhgVerificationEexiFactorClientModel, isCurrent: boolean, isEditable: boolean): string {

        const propValue = isCurrent ? "currentValue" : (factor.revisedValue !== null ? "revisedValue" : "submittedValue");

        return this.ghgEexiFactorsRenderer.filtersHtml(factor, propValue, isEditable);
    }

    public filtersCiiHtml(factor: GhgVerificationCiiFactorClientModel, isCurrent: boolean, isEditable: boolean): string {

        const propValue = isCurrent ? "currentValue" : (factor.revisedValue !== null ? "revisedValue" : "submittedValue");

        return this.ghgCiiFactorsRenderer.filtersHtml(factor, propValue, isEditable);
    }

    async submit() {
        try {

            this.meta.sisterVesselIds = this.sisterVessels.filter(x => x.isSelected).map(x => x.vesselId);
            this.meta.ghgVerificationEvdiStatusId = this.ghgVerificationEvdiStatusId;

            const meta = JSON.stringify(this.meta);
            await this.ghgService.changeStatus(this.ghgVerification.ghgVerificationId, this.currentGhgVerificationStatus.id, this.comments, meta, this.subtaskTypes, this.statusSnippet, this.sendEmails);

            if (this.onSubmit) {
                this.onSubmit();
            }

            this.dialogClientService.hide();
        } catch (e) {
            console.log("Failed to change ghg verification status", e);
        }
    }
}